/* Epic Braceletz — bundle buttons + pick-your-own modal
   Adds real product variants to the cart directly (via /cart/add.js),
   so Shopify's native inventory tracking handles every single
   bracelet SKU and the full-pack SKU with no extra app required.
   Pricing for Pick 4 / Pick 2 is handled by Shopify automatic
   discounts configured in the admin — see the setup notes that
   were shared alongside this file.

   Each Pick 4 / Pick 2 add tags its line items with two hidden cart
   properties (underscore-prefixed properties are automatically
   hidden from the customer-facing cart/checkout):
     _bundle_id   — shared by every item in that one bundle instance
     _bundle_size — how many items that bundle instance should have
   assets/braceletz-bundle-linking.js watches the cart site-wide and
   removes an entire bundle group the moment it's no longer at full
   size, so a customer can never end up checking out with a lone
   leftover bracelet.

   The full-pack "All 7" purchase uses Shopify's real dynamic
   checkout button (a {% form 'product' %} + {{ form | payment_button }}
   in the snippet itself). */

(function () {
  'use strict';

  function EpicBraceletzBundles(root) {
    this.root = root;
    this.busy = false;
    this.selected = new Set();
    this.pickCount = 2;
    this.lockedScrollY = 0;

    var dataEl = root.querySelector('[data-epic-bz-json]');
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Epic Braceletz bundles: could not parse product data.', e);
      this.data = { fullPack: {}, singles: [] };
    }

    this.modal = root.querySelector('.epic-bz-modal');
    this.grid = root.querySelector('.epic-bz-grid');
    this.titleEl = root.querySelector('.epic-bz-modal__title');
    this.counterEl = root.querySelector('.epic-bz-modal__counter');
    this.addBtn = root.querySelector('.epic-bz-modal__add');
    this.addBtnLabel = this.addBtn ? this.addBtn.querySelector('[data-epic-bz-add-label]') : null;
    this.errorEl = root.querySelector('.epic-bz-modal__error');
    this.overlay = root.querySelector('.epic-bz-modal__overlay');
    this.closeBtn = root.querySelector('.epic-bz-modal__close');

    this.fullPackBtn = root.querySelector('[data-epic-bz-trigger="7"]');
    this.pick4Btn = root.querySelector('[data-epic-bz-trigger="4"]');
    this.pick2Btn = root.querySelector('[data-epic-bz-trigger="2"]');

    this.videosWarmed = false;
    this.bindEvents();
    this.scheduleVideoWarmUp();
  }

  EpicBraceletzBundles.prototype.bindEvents = function () {
    var self = this;

    if (this.fullPackBtn) {
      this.fullPackBtn.addEventListener('click', function () {
        self.addFullPackToCart();
      });
    }
    if (this.pick4Btn) {
      this.pick4Btn.addEventListener('click', function () {
        self.openModal(4);
      });
      this.pick4Btn.addEventListener('mouseenter', function () {
        self.warmUpVideos();
      });
      this.pick4Btn.addEventListener('touchstart', function () {
        self.warmUpVideos();
      }, { passive: true });
    }
    if (this.pick2Btn) {
      this.pick2Btn.addEventListener('click', function () {
        self.openModal(2);
      });
      this.pick2Btn.addEventListener('mouseenter', function () {
        self.warmUpVideos();
      });
      this.pick2Btn.addEventListener('touchstart', function () {
        self.warmUpVideos();
      }, { passive: true });
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function () {
        self.closeModal();
      });
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', function () {
        self.closeModal();
      });
    }
    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && self.modal && self.modal.classList.contains('is-open')) {
        self.closeModal();
      }
    });
    if (this.addBtn) {
      this.addBtn.addEventListener('click', function () {
        self.addSelectedToCart();
      });
    }

    // Tiles are real <button> elements, so Enter/Space already fire a
    // native click — a separate keydown handler here would double-toggle.
    var tiles = this.grid ? this.grid.querySelectorAll('.epic-bz-tile') : [];
    tiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        self.toggleTile(tile);
      });
    });
  };

  EpicBraceletzBundles.prototype.openModal = function (count) {
    this.pickCount = count;
    this.selected.clear();
    this.clearError();

    if (this.titleEl) this.titleEl.textContent = 'Pick ' + count + '!';
    if (this.addBtnLabel) {
      var priceLabel = count === 4 ? this.data.pick4PriceLabel : this.data.pick2PriceLabel;
      this.addBtnLabel.textContent = 'Add to Cart — ' + priceLabel;
    }

    var self = this;
    var tiles = this.grid.querySelectorAll('.epic-bz-tile');
    tiles.forEach(function (tile) {
      tile.classList.remove('is-selected');
      var single = self.data.singles[tile.dataset.index];
      var soldOut = !single || !single.available;
      tile.classList.toggle('is-soldout', soldOut);
      tile.classList.toggle('is-disabled', soldOut);
      tile.setAttribute('aria-pressed', 'false');
    });

    this.updateCounter();
    this.lockScroll();
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    this.playVideos();

    if (this.closeBtn) this.closeBtn.focus();
  };

  EpicBraceletzBundles.prototype.closeModal = function () {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.unlockScroll();
    this.pauseVideos();
  };

  // A plain `overflow: hidden` on <body> doesn't reliably block background
  // scroll on iOS Safari, and can even make fixed-position content (like
  // this modal's footer) end up unreachable. Locking with position: fixed
  // and restoring the scroll offset afterwards is the robust fix — but it
  // requires capturing/restoring scrollY ourselves so the page doesn't jump.
  EpicBraceletzBundles.prototype.lockScroll = function () {
    this.lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '-' + this.lockedScrollY + 'px';
    document.body.classList.add('epic-bz-lock-scroll');
  };

  EpicBraceletzBundles.prototype.unlockScroll = function () {
    document.body.classList.remove('epic-bz-lock-scroll');
    document.body.style.top = '';
    window.scrollTo(0, this.lockedScrollY);
  };

  // Sets src + calls load() (without playing) so the browser starts
  // buffering ahead of time — called from idle time after the page's
  // own critical content is done, and again on hover/touchstart of the
  // Pick 4 / Pick 2 buttons as a earlier trigger in case idle time
  // hasn't fired yet. Idempotent: once a video has a real src, later
  // calls are no-ops for it.
  EpicBraceletzBundles.prototype.warmUpVideos = function () {
    if (this.videosWarmed) return;
    this.videosWarmed = true;
    var videos = this.grid.querySelectorAll('video');
    videos.forEach(function (video) {
      if (video.getAttribute('src') || !video.dataset.src) return;
      video.preload = 'auto';
      video.setAttribute('src', video.dataset.src);
      video.load();
    });
  };

  EpicBraceletzBundles.prototype.scheduleVideoWarmUp = function () {
    var self = this;
    var run = function () {
      self.warmUpVideos();
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 1500);
    }
  };

  EpicBraceletzBundles.prototype.playVideos = function () {
    var videos = this.grid.querySelectorAll('video');
    videos.forEach(function (video) {
      if (!video.getAttribute('src') && video.dataset.src) {
        video.preload = 'auto';
        video.setAttribute('src', video.dataset.src);
        video.load();
      }
      video.muted = true;
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          /* autoplay blocked — poster image stays visible, no-op */
        });
      }
    });
  };

  EpicBraceletzBundles.prototype.pauseVideos = function () {
    var videos = this.grid.querySelectorAll('video');
    videos.forEach(function (video) {
      video.pause();
    });
  };

  EpicBraceletzBundles.prototype.toggleTile = function (tile) {
    var isSelected = tile.classList.contains('is-selected');

    if (tile.classList.contains('is-disabled') && !isSelected) return;

    var idx = tile.dataset.index;

    if (isSelected) {
      tile.classList.remove('is-selected');
      tile.setAttribute('aria-pressed', 'false');
      this.selected.delete(idx);
    } else {
      if (this.selected.size >= this.pickCount) return;
      tile.classList.add('is-selected');
      tile.setAttribute('aria-pressed', 'true');
      this.selected.add(idx);
    }

    this.updateCounter();
    this.refreshDisabledState();
  };

  EpicBraceletzBundles.prototype.refreshDisabledState = function () {
    var self = this;
    var atMax = this.selected.size >= this.pickCount;
    var tiles = this.grid.querySelectorAll('.epic-bz-tile');
    tiles.forEach(function (tile) {
      var single = self.data.singles[tile.dataset.index];
      var soldOut = !single || !single.available;
      var isSelected = tile.classList.contains('is-selected');
      tile.classList.toggle('is-disabled', soldOut || (atMax && !isSelected));
    });
  };

  EpicBraceletzBundles.prototype.updateCounter = function () {
    if (!this.counterEl) return;
    var ready = this.selected.size === this.pickCount;
    this.counterEl.innerHTML =
      '<span class="' + (ready ? 'epic-bz-count-done' : '') + '">' +
      this.selected.size + ' of ' + this.pickCount + ' selected' +
      '</span>';
    if (this.addBtn) this.addBtn.disabled = !ready;
  };

  EpicBraceletzBundles.prototype.addFullPackToCart = function () {
    if (!this.data.fullPack || !this.data.fullPack.variantId) return;
    // Not tagged with a _bundle_id: this is a single line item on its
    // own SKU, there's nothing else it needs to stay linked to.
    this.submitToCart(
      [{ id: this.data.fullPack.variantId, quantity: 1 }],
      this.fullPackBtn
    );
  };

  EpicBraceletzBundles.prototype.addSelectedToCart = function () {
    var self = this;
    var bundleId = 'bz-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
    var bundleLabel = (this.pickCount === 4 ? 'Pick 4' : 'Pick 2') + ' Bundle';
    var items = Array.from(this.selected).map(function (idx) {
      return {
        id: self.data.singles[idx].variantId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_label: bundleLabel,
          _bundle_size: self.pickCount
        }
      };
    });
    if (items.length !== this.pickCount) return;
    this.submitToCart(items, this.addBtn);
  };

  EpicBraceletzBundles.prototype.showError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.add('is-visible');
  };

  EpicBraceletzBundles.prototype.clearError = function () {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.classList.remove('is-visible');
  };

  EpicBraceletzBundles.prototype.submitToCart = function (items, triggerBtn) {
    if (this.busy) return;
    this.busy = true;
    this.clearError();
    if (triggerBtn) triggerBtn.classList.add('is-loading');

    var cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    var sections = [];
    if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
      sections = cartEl.getSectionsToRender().map(function (section) {
        return section.id;
      });
    }

    var body = JSON.stringify({
      items: items,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    })
      .then(function (response) {
        return response.json();
      })
      .then((function (self) {
        return function (response) {
          if (response.status) {
            self.showError(response.description || response.message || 'That combination is not available — please try again.');
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'epic-braceletz-bundles',
                message: response.message,
                errors: response.description
              });
            }
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'epic-braceletz-bundles' });
          }

          if (cartEl && typeof cartEl.renderContents === 'function') {
            cartEl.renderContents(response);
            if (cartEl.classList.contains('is-empty')) cartEl.classList.remove('is-empty');
            if (typeof cartEl.open === 'function') cartEl.open();
          } else {
            window.location.href = window.routes.cart_url;
            return;
          }

          self.closeModal();
        };
      })(this))
      .catch((function (self) {
        return function (err) {
          console.error('Epic Braceletz bundles: add to cart failed.', err);
          self.showError('Something went wrong adding that to your cart — please try again.');
        };
      })(this))
      .finally((function (self) {
        return function () {
          self.busy = false;
          if (triggerBtn) triggerBtn.classList.remove('is-loading');
        };
      })(this));
  };

  function init() {
    document.querySelectorAll('[data-epic-braceletz-bundles]').forEach(function (root) {
      new EpicBraceletzBundles(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
